import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { barTabService, orderService, packageService, productService } from '../services/api';
import { useSocket } from '../hooks/useSocket';

const MONEY = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const METHOD_LABEL = {
  cash: 'Dinheiro',
  pix: 'PIX',
  card_debit: 'Cartão de débito',
  card_credit: 'Cartão de crédito',
  mercadopago: 'Mercado Pago',
};
const paymentSplits = (total, cashValue, otherMethod) => {
  const cents = Math.round(Number(total || 0) * 100);
  const cash = Math.max(0, Math.min(cents, Math.round(Number(cashValue || 0) * 100)));
  return [cash && { payment_method: 'cash', amount: cash / 100 }, cents > cash && { payment_method: otherMethod, amount: (cents - cash) / 100 }].filter(Boolean);
};

export default function Orders() {
  const [section, setSection] = useState('tabs');
  const [orders, setOrders] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [tabStatus, setTabStatus] = useState('open');
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [newTab, setNewTab] = useState({ label: '', customer_name: '', notes: '' });
  const [addForm, setAddForm] = useState({ product_id: '', quantity: 1 });
  const [closeForm, setCloseForm] = useState({ cash_amount: '', other_method: 'pix', discount: '' });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const [queueRes, tabsRes, productRes, packageRes] = await Promise.all([
        orderService.getQueue(),
        barTabService.getAll(tabStatus),
        productService.getAll(),
        packageService.getAll(),
      ]);
      setOrders(queueRes.data || []);
      setTabs(tabsRes.data || []);
      setProducts(productRes.data || []);
      setPackages(packageRes.data || []);
    } catch (err) {
      setMessage(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [tabStatus]);

  const refreshSelected = useCallback(async (id) => {
    if (!id) return;
    try { setSelected((await barTabService.get(id)).data); }
    catch (err) { setMessage(errorText(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useSocket((event, data) => {
    if (event === 'snack:order_created' || event === 'snack:order_updated' || event === 'bar:tab_updated') {
      load();
      if (event === 'bar:tab_updated' && Number(data?.id) === Number(selected?.id)) refreshSelected(selected.id);
    }
  });
  useEffect(() => {
    const onKey = event => {
      if (event.key !== 'Escape') return;
      if (modal) setModal(null);
      else if (selected) setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal, selected]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR');
    return products.filter(p => !term || `${p.name} ${p.category || ''}`.toLocaleLowerCase('pt-BR').includes(term));
  }, [products, search]);

  const run = async (action, success) => {
    setBusy(true);
    setMessage('');
    try {
      const result = await action();
      if (success) setMessage(success);
      return result;
    } catch (err) {
      setMessage(errorText(err));
      return null;
    } finally { setBusy(false); }
  };

  const move = async (id, status) => {
    const result = await run(() => orderService.setStatus(id, status));
    if (result) load();
  };

  const createTab = async () => {
    const result = await run(() => barTabService.create(newTab), 'Comanda aberta com sucesso.');
    if (!result) return;
    setModal(null);
    setNewTab({ label: '', customer_name: '', notes: '' });
    setTabStatus('open');
    await load();
    setSelected(result.data);
  };

  const openTab = async id => {
    setMessage('');
    const result = await run(() => barTabService.get(id));
    if (result) setSelected(result.data);
  };

  const addItem = async () => {
    if (!addForm.product_id) return setMessage('Escolha um produto.');
    const result = await run(
      () => barTabService.addItem(selected.id, {
        product_id: Number(addForm.product_id),
        quantity: Number(addForm.quantity) || 1,
      }),
      'Produto adicionado à comanda.'
    );
    if (!result) return;
    setSelected(result.data);
    setAddForm({ product_id: '', quantity: 1 });
    setSearch('');
    load();
  };

  const addPackageItem = async (packageId, quantity) => {
    const result = await run(
      () => barTabService.addItem(selected.id, {
        package_id: Number(packageId),
        quantity: Number(quantity) || 1,
      }),
      'Pacote adicionado à comanda.'
    );
    if (!result) return;
    setSelected(result.data);
    load();
  };

  const addProductQuick = async product => {
    const result = await run(
      () => barTabService.addItem(selected.id, { product_id: product.id, quantity: 1 }),
      `${product.name} adicionado à comanda.`
    );
    if (result) {
      setSelected(result.data);
      load();
    }
  };

  const removeItem = async item => {
    if (!window.confirm(`Retirar ${item.product_name} da comanda?`)) return;
    const result = await run(() => barTabService.removeItem(selected.id, item.id), 'Item retirado da comanda.');
    if (result) {
      setSelected(result.data);
      load();
    }
  };

  const closeTab = async () => {
    const total = Math.max(0, Number(selected.subtotal || 0) - (Number(closeForm.discount) || 0));
    const result = await run(
      () => barTabService.close(selected.id, {
        payment_splits: paymentSplits(total, closeForm.cash_amount, closeForm.other_method),
        discount: Number(closeForm.discount) || 0,
      }),
      'Comanda fechada e pagamento registrado no financeiro.'
    );
    if (!result) return;
    setSelected(null);
    setModal(null);
    setCloseForm({ cash_amount: '', other_method: 'pix', discount: '' });
    await load();
  };

  const cancelTab = async () => {
    if (!window.confirm('Cancelar esta comanda? Todos os itens voltarão para o estoque.')) return;
    const result = await run(() => barTabService.cancel(selected.id), 'Comanda cancelada. O estoque foi devolvido.');
    if (!result) return;
    setSelected(null);
    await load();
  };

  return <div style={s.page}>
    <header style={s.head}>
      <div>
        <h1 style={s.title}>Lanchonete</h1>
        <p style={s.sub}>Pedidos dos computadores e comandas de mesas</p>
      </div>
      <div style={s.headActions}>
        <button style={s.refresh} onClick={load}>↻ Atualizar</button>
        {section === 'tabs' && <button style={s.primary} onClick={() => setModal('new')}>+ Abrir comanda</button>}
      </div>
    </header>

    <nav style={s.sections}>
      <button style={{ ...s.section, ...(section === 'tabs' ? s.sectionOn : {}) }} onClick={() => setSection('tabs')}>
        🧾 Comandas <span style={s.badge}>{tabs.filter(t => t.status === 'open').length}</span>
      </button>
      <button style={{ ...s.section, ...(section === 'queue' ? s.sectionOn : {}) }} onClick={() => setSection('queue')}>
        🔔 Pedidos dos PCs <span style={s.badge}>{orders.length}</span>
      </button>
    </nav>

    {message && <div style={s.message}>{message}<button onClick={() => setMessage('')} style={s.messageClose}>×</button></div>}
    {loading && <div style={s.empty}>Carregando…</div>}

    {!loading && section === 'queue' && <Queue orders={orders} move={move} busy={busy} />}

    {!loading && section === 'tabs' && <>
      <div style={s.filterBar}>
        <button style={{ ...s.filter, ...(tabStatus === 'open' ? s.filterOn : {}) }} onClick={() => setTabStatus('open')}>Abertas</button>
        <button style={{ ...s.filter, ...(tabStatus === 'closed' ? s.filterOn : {}) }} onClick={() => setTabStatus('closed')}>Fechadas</button>
        <button style={{ ...s.filter, ...(tabStatus === 'cancelled' ? s.filterOn : {}) }} onClick={() => setTabStatus('cancelled')}>Canceladas</button>
      </div>
      {!tabs.length && <div style={s.empty}>{tabStatus === 'open' ? 'Nenhuma comanda aberta.' : 'Nenhuma comanda encontrada.'}</div>}
      <div style={s.tabGrid}>{tabs.map(tab => <button type="button" key={tab.id} style={s.tabCard} onClick={() => openTab(tab.id)}>
        <div style={s.tabTop}>
          <span style={statusStyle(tab.status)}>{statusLabel(tab.status)}</span>
          <small style={s.tabNumber}>#{tab.id}</small>
        </div>
        <strong style={s.tabName}>{tab.label}</strong>
        <span style={s.customer}>{tab.customer_name || tab.client_name || tab.nickname || 'Cliente não informado'}</span>
        <div style={s.tabMeta}>
          <span>{Number(tab.item_quantity || 0)} item(ns)</span>
          <strong>{MONEY.format(Number(tab.total || 0))}</strong>
        </div>
        <small style={s.opened}>Aberta por {tab.opened_by_name} · {dateTime(tab.opened_at)}</small>
      </button>)}</div>
    </>}

    {selected && <TabDetail
      tab={selected}
      products={filteredProducts}
      packages={packages}
      search={search}
      setSearch={setSearch}
      addForm={addForm}
      setAddForm={setAddForm}
      addItem={addItem}
      addPackageItem={addPackageItem}
      addProductQuick={addProductQuick}
      removeItem={removeItem}
      close={() => setModal('close')}
      cancel={cancelTab}
      dismiss={() => setSelected(null)}
      busy={busy}
    />}

    {modal === 'new' && <Modal title="Abrir nova comanda" onClose={() => setModal(null)}>
      <div style={s.two}>
        <Field label="Mesa ou identificação *"><input autoFocus style={s.input} placeholder="Ex.: Mesa 01" value={newTab.label} onChange={e => setNewTab({ ...newTab, label: e.target.value })}/></Field>
        <Field label="Nome do cliente"><input style={s.input} placeholder="Opcional" value={newTab.customer_name} onChange={e => setNewTab({ ...newTab, customer_name: e.target.value })}/></Field>
      </div>
      <Field label="Observações"><input style={s.input} placeholder="Ex.: aniversário, responsável, telefone…" value={newTab.notes} onChange={e => setNewTab({ ...newTab, notes: e.target.value })}/></Field>
      <div style={s.modalActions}>
        <button style={s.secondary} onClick={() => setModal(null)}>Cancelar</button>
        <button style={s.primary} disabled={busy || !newTab.label.trim()} onClick={createTab}>{busy ? 'Abrindo…' : 'Abrir comanda'}</button>
      </div>
    </Modal>}

    {modal === 'close' && selected && <Modal title={`Fechar ${selected.label}`} onClose={() => setModal(null)}>
      <div style={s.closeSummary}>
        <span>Subtotal</span><strong>{MONEY.format(Number(selected.subtotal || 0))}</strong>
      </div>
      <div style={s.two}>
        <Field label="Desconto (R$)"><input type="number" min="0" step="0.01" style={s.input} value={closeForm.discount} onChange={e => setCloseForm({ ...closeForm, discount: e.target.value })}/></Field>
        <Field label="Valor em dinheiro"><input type="number" min="0" step="0.01" style={s.input} value={closeForm.cash_amount} onChange={e => setCloseForm({ ...closeForm, cash_amount: e.target.value })}/></Field>
      </div>
      <div style={s.totalFinal}><span>TOTAL FINAL</span><strong>{MONEY.format(Math.max(0, Number(selected.subtotal || 0) - (Number(closeForm.discount) || 0)))}</strong></div>
      <div style={s.two}>
        <Field label="Restante calculado"><input readOnly style={s.input} value={MONEY.format(Math.max(0, Number(selected.subtotal || 0) - (Number(closeForm.discount) || 0) - (Number(closeForm.cash_amount) || 0)))}/></Field>
        <Field label="Forma do restante"><select style={s.input} value={closeForm.other_method} onChange={e => setCloseForm({ ...closeForm, other_method: e.target.value })}><option value="pix">PIX</option><option value="card_debit">Cartão de débito</option><option value="card_credit">Cartão de crédito</option></select></Field>
      </div>
      <p style={s.hint}>Ao confirmar, o pagamento entra no caixa e no financeiro. Essa operação não pode ser repetida.</p>
      <div style={s.modalActions}>
        <button style={s.secondary} onClick={() => setModal(null)}>Voltar</button>
        <button style={s.primary} disabled={busy} onClick={closeTab}>{busy ? 'Fechando…' : 'Confirmar pagamento'}</button>
      </div>
    </Modal>}
  </div>;
}

function Queue({ orders, move, busy }) {
  if (!orders.length) return <div style={s.empty}>Nenhum pedido aguardando atendimento.</div>;
  return <div style={s.grid}>{orders.map(o => <article key={o.id} style={s.card}>
    <div style={s.number}>PEDIDO #{o.id}</div>
    <div style={s.product}>{o.product_name}</div>
    <div style={s.info}><strong>{o.client_name || o.nickname || 'Cliente'}</strong><span>{o.machine_name || 'PC não informado'}</span></div>
    <div style={s.meta}><span>{o.quantity} un.</span><span>{MONEY.format(Number(o.total))}</span></div>
    <div style={s.actions}>
      {o.order_status === 'pending' && <button disabled={busy} style={s.prepare} onClick={() => move(o.id, 'preparing')}>Aceitar</button>}
      <button disabled={busy} style={s.deliver} onClick={() => move(o.id, 'delivered')}>Entregue</button>
      <button disabled={busy} style={s.cancel} onClick={() => move(o.id, 'cancelled')}>Cancelar</button>
    </div>
  </article>)}</div>;
}

function TabDetail({ tab, products, packages, search, setSearch, addForm, setAddForm, addItem, addPackageItem, addProductQuick, removeItem, close, cancel, dismiss, busy }) {
  const isOpen = tab.status === 'open';
  const [catalogTab, setCatalogTab] = useState('products'); // 'products' | 'packages'
  const [pkgQuantities, setPkgQuantities] = useState({});

  return <div style={s.overlay} onMouseDown={e => e.target === e.currentTarget && dismiss()}>
    <section style={s.detail}>
      <header style={s.detailHead}>
        <div>
          <span style={statusStyle(tab.status)}>{statusLabel(tab.status)} · COMANDA #{tab.id}</span>
          <h2 style={s.detailTitle}>{tab.label}</h2>
          <p style={s.detailSub}>{tab.customer_name || tab.client_name || 'Cliente não informado'}{tab.notes ? ` · ${tab.notes}` : ''}</p>
        </div>
        <button style={s.closeX} onClick={dismiss}>×</button>
      </header>

      <div style={s.detailColumns}>
        <div style={s.itemsPanel}>
          <h3 style={s.panelTitle}>Itens da comanda</h3>
          {!tab.items?.length && <div style={s.itemsEmpty}>Nenhum item adicionado.</div>}
          {(tab.items || []).map(item => <div key={item.id} style={s.itemRow}>
            {item.item_type === 'package'
              ? <div style={s.itemFallback}>⏱️</div>
              : (item.image_url ? <img src={item.image_url} alt="" style={s.itemImage}/> : <div style={s.itemFallback}>🍔</div>)}
            <div style={s.itemCopy}>
              <strong>{item.product_name}</strong>
              <small>{item.quantity} × {MONEY.format(Number(item.unit_price))}</small>
            </div>
            <strong style={s.itemTotal}>{MONEY.format(Number(item.total))}</strong>
            {isOpen && <button title="Retirar item" style={s.remove} disabled={busy} onClick={() => removeItem(item)}>×</button>}
          </div>)}
          <div style={s.summaryLine}><span>Subtotal</span><strong>{MONEY.format(Number(tab.subtotal || 0))}</strong></div>
          {Number(tab.discount) > 0 && <div style={s.summaryLine}><span>Desconto</span><strong>− {MONEY.format(Number(tab.discount))}</strong></div>}
          <div style={s.summaryTotal}><span>Total</span><strong>{MONEY.format(Number(tab.total || 0))}</strong></div>
          {!isOpen && tab.payment_method && <div style={s.paidWith}>Pago com {METHOD_LABEL[tab.payment_method] || tab.payment_method} em {dateTime(tab.closed_at)}</div>}
        </div>

        {isOpen && <div style={s.catalogPanel}>
          <div style={{display: 'flex', gap: 6, marginBottom: 12}}>
            <button
              type="button"
              style={{ ...s.filter, ...(catalogTab === 'products' ? s.filterOn : {}) }}
              onClick={() => setCatalogTab('products')}
            >
              🍔 PRODUTOS
            </button>
            <button
              type="button"
              style={{ ...s.filter, ...(catalogTab === 'packages' ? s.filterOn : {}) }}
              onClick={() => setCatalogTab('packages')}
            >
              ⏱️ HORAS / PACOTES
            </button>
          </div>

          {catalogTab === 'products' ? (
            <>
              <input style={s.search} placeholder="Pesquisar produto…" value={search} onChange={e => setSearch(e.target.value)}/>
              <div style={s.productGrid}>{products.map(product => <button type="button" key={product.id} style={s.productChoice} disabled={busy || Number(product.stock) < 1} onClick={() => addProductQuick(product)}>
                {product.image_url ? <img src={product.image_url} alt="" style={s.productImage}/> : <div style={s.productFallback}>🍔</div>}
                <span style={s.productChoiceCopy}><strong>{product.name}</strong><small>{MONEY.format(Number(product.price))} · estoque {product.stock}</small></span>
                <span style={s.add}>+</span>
              </button>)}</div>
              {!products.length && <div style={s.itemsEmpty}>Nenhum produto disponível.</div>}
              <div style={s.manualAdd}>
                <select style={{ ...s.input, flex: 1 }} value={addForm.product_id} onChange={e => setAddForm({ ...addForm, product_id: e.target.value })}>
                  <option value="">Produto</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} · {MONEY.format(Number(p.price))}</option>)}
                </select>
                <input aria-label="Quantidade" type="number" min="1" style={s.qty} value={addForm.quantity} onChange={e => setAddForm({ ...addForm, quantity: e.target.value })}/>
                <button style={s.primary} disabled={busy || !addForm.product_id} onClick={addItem}>Adicionar</button>
              </div>
            </>
          ) : (
            <div style={s.packagesList}>
              <div style={{ color: '#94a3b8', fontSize: 12, padding: '2px 2px 6px' }}>
                💡 Aqui o pacote entra só na cobrança da comanda. As horas devem ser lançadas no controle de horas — não são creditadas de novo ao fechar.
              </div>
              {(packages || []).map(pkg => {
                const qty = pkgQuantities[pkg.id] || 1;
                return (
                  <div key={pkg.id} style={s.packageRow}>
                    <div style={s.packageInfo}>
                      <strong style={{fontSize: 15, color: '#fff'}}>{pkg.name}</strong>
                      <small style={{color: '#a3e635', fontSize: 13, fontWeight: 700}}>
                        ⏱️ {pkg.minutes} min · {MONEY.format(Number(pkg.price))}
                      </small>
                    </div>
                    <div style={s.packageActions}>
                      <input
                        type="number"
                        min="1"
                        style={s.qtyInput}
                        value={qty}
                        onChange={e => setPkgQuantities({ ...pkgQuantities, [pkg.id]: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                      />
                      <button
                        type="button"
                        style={s.primarySmall}
                        disabled={busy}
                        onClick={() => addPackageItem(pkg.id, qty)}
                      >
                        + Adicionar
                      </button>
                    </div>
                  </div>
                );
              })}
              {!(packages || []).length && <div style={s.itemsEmpty}>Nenhum pacote cadastrado.</div>}
            </div>
          )}
        </div>}
      </div>

      <footer style={s.detailFooter}>
        {isOpen && <button style={s.danger} disabled={busy} onClick={cancel}>Cancelar comanda</button>}
        <span style={s.footerSpacer}/>
        <button style={s.secondary} onClick={dismiss}>Voltar</button>
        {isOpen && <button style={s.primary} disabled={busy || !tab.items?.length} onClick={close}>Fechar comanda</button>}
      </footer>
    </section>
  </div>;
}

function Modal({ title, children, onClose }) {
  return <div style={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
    <section style={s.modal}>
      <header style={s.modalHead}><h2 style={s.modalTitle}>{title}</h2><button style={s.closeX} onClick={onClose}>×</button></header>
      {children}
    </section>
  </div>;
}

function Field({ label, children }) {
  return <label style={s.field}><span style={s.label}>{label}</span>{children}</label>;
}

function errorText(err) {
  return err?.response?.data?.error || err?.message || 'Não foi possível concluir a operação.';
}
function dateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
function statusLabel(status) {
  return { open: 'ABERTA', closed: 'FECHADA', cancelled: 'CANCELADA' }[status] || status;
}
function statusStyle(status) {
  const colors = {
    open: { color: '#bef264', background: '#28420d' },
    closed: { color: '#67e8f9', background: '#103844' },
    cancelled: { color: '#fca5a5', background: '#4b1717' },
  };
  return { ...s.status, ...(colors[status] || colors.open) };
}

const s = {
  page:{maxWidth:1500,margin:'0 auto',overflowX:'hidden'},
  head:{display:'flex',justifyContent:'space-between',alignItems:'center',gap:16,marginBottom:18,flexWrap:'wrap'},
  title:{color:'#fff',fontSize:26,margin:0},
  sub:{color:'#71717a',margin:'5px 0 0'},
  headActions:{display:'flex',gap:9,flexWrap:'wrap'},
  refresh:{background:'transparent',border:'1px solid #3f6212',color:'#bef264',borderRadius:9,padding:'10px 15px',cursor:'pointer',fontWeight:800},
  primary:{background:'#a3e635',color:'#111',border:0,borderRadius:9,padding:'11px 16px',cursor:'pointer',fontWeight:900},
  secondary:{background:'#20202d',color:'#e4e4e7',border:'1px solid #3f3f50',borderRadius:9,padding:'10px 15px',cursor:'pointer',fontWeight:800},
  danger:{background:'transparent',color:'#fca5a5',border:'1px solid #7f1d1d',borderRadius:9,padding:'10px 15px',cursor:'pointer',fontWeight:800},
  sections:{display:'flex',gap:8,borderBottom:'1px solid #292939',marginBottom:18,overflowX:'auto',scrollbarWidth:'none'},
  section:{display:'flex',alignItems:'center',gap:8,whiteSpace:'nowrap',background:'transparent',color:'#8f8f9d',border:0,borderBottom:'3px solid transparent',padding:'12px 16px',fontWeight:900,cursor:'pointer'},
  sectionOn:{color:'#d9f99d',borderBottomColor:'#a3e635'},
  badge:{background:'#292938',color:'#fff',borderRadius:999,padding:'2px 7px',fontSize:11},
  filterBar:{display:'flex',gap:7,marginBottom:15,flexWrap:'wrap'},
  filter:{background:'#181824',color:'#8f8f9d',border:'1px solid #303044',borderRadius:8,padding:'8px 13px',cursor:'pointer',fontWeight:800},
  filterOn:{color:'#111',background:'#a3e635',borderColor:'#a3e635'},
  message:{position:'relative',background:'#17230e',border:'1px solid #4d7c0f',color:'#d9f99d',borderRadius:10,padding:'11px 42px 11px 14px',marginBottom:15,fontWeight:700},
  messageClose:{position:'absolute',right:10,top:5,background:'transparent',border:0,color:'#d9f99d',fontSize:24,cursor:'pointer'},
  tabGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(250px,1fr))',gap:13},
  tabCard:{textAlign:'left',background:'#14141f',border:'1px solid #34344e',borderRadius:14,padding:17,cursor:'pointer',minWidth:0},
  tabTop:{display:'flex',justifyContent:'space-between',alignItems:'center'},
  status:{display:'inline-block',fontSize:10,fontWeight:1000,letterSpacing:1,borderRadius:999,padding:'5px 9px'},
  tabNumber:{color:'#6b7280',fontWeight:800},
  tabName:{display:'block',color:'#fff',fontSize:21,margin:'14px 0 5px',overflowWrap:'anywhere'},
  customer:{display:'block',color:'#9292a2',fontSize:13,minHeight:19},
  tabMeta:{display:'flex',justifyContent:'space-between',alignItems:'center',borderTop:'1px solid #292939',marginTop:13,paddingTop:12,color:'#9ca3af'},
  opened:{display:'block',color:'#5f5f70',fontSize:10,marginTop:10},
  grid:{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))',gap:15},
  card:{background:'#14141f',border:'1px solid #34344e',borderRadius:13,padding:18},
  number:{color:'#a3e635',fontSize:11,fontWeight:900,letterSpacing:1.5},
  product:{color:'#fff',fontSize:23,fontWeight:900,margin:'9px 0 13px'},
  info:{display:'flex',justifyContent:'space-between',gap:8,color:'#e5e7eb',fontSize:13},
  meta:{display:'flex',justifyContent:'space-between',color:'#9ca3af',fontSize:13,borderTop:'1px solid #292939',marginTop:13,paddingTop:11},
  actions:{display:'flex',gap:7,marginTop:15,flexWrap:'wrap'},
  prepare:{flex:1,background:'#f59e0b',border:0,borderRadius:7,padding:9,fontWeight:800,cursor:'pointer'},
  deliver:{flex:1,background:'#a3e635',border:0,borderRadius:7,padding:9,fontWeight:800,cursor:'pointer'},
  cancel:{background:'transparent',color:'#f87171',border:'1px solid #7f1d1d',borderRadius:7,padding:9,cursor:'pointer'},
  empty:{color:'#71717a',background:'#14141f',border:'1px solid #292939',borderRadius:12,padding:30,textAlign:'center'},
  overlay:{position:'fixed',inset:0,zIndex:1200,background:'rgba(0,0,0,.78)',display:'flex',alignItems:'center',justifyContent:'center',padding:12},
  detail:{width:'min(1000px,100%)',maxHeight:'calc(100dvh - 24px)',overflowY:'auto',overflowX:'hidden',background:'#101018',border:'1px solid #39394d',borderRadius:14,boxShadow:'0 26px 68px rgba(0,0,0,.55)'},
  detailHead:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:13,padding:'17px 19px',borderBottom:'1px solid #292939'},
  detailTitle:{color:'#fff',fontSize:22,margin:'7px 0 3px'},
  detailSub:{color:'#8b8b9b',margin:0},
  closeX:{background:'#20202d',border:'1px solid #3b3b4d',color:'#fff',borderRadius:8,width:38,height:38,fontSize:24,cursor:'pointer',flex:'0 0 auto'},
  detailColumns:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,330px),1fr))',gap:13,padding:15},
  itemsPanel:{background:'#15151f',border:'1px solid #303043',borderRadius:10,padding:13,minWidth:0},
  catalogPanel:{background:'#15151f',border:'1px solid #303043',borderRadius:10,padding:13,minWidth:0},
  panelTitle:{color:'#fff',fontSize:14,margin:'0 0 11px'},
  itemRow:{display:'grid',gridTemplateColumns:'38px minmax(0,1fr) auto 28px',alignItems:'center',gap:8,padding:'8px 0',borderBottom:'1px solid #292939'},
  itemImage:{width:38,height:38,objectFit:'cover',borderRadius:7},
  itemFallback:{width:38,height:38,display:'grid',placeItems:'center',background:'#242432',borderRadius:7},
  itemCopy:{display:'flex',flexDirection:'column',minWidth:0,color:'#fff'},
  itemTotal:{color:'#d9f99d',whiteSpace:'nowrap'},
  remove:{width:28,height:28,borderRadius:7,background:'transparent',color:'#fca5a5',border:'1px solid #7f1d1d',cursor:'pointer'},
  itemsEmpty:{color:'#71717a',padding:'20px 8px',textAlign:'center'},
  summaryLine:{display:'flex',justifyContent:'space-between',color:'#a1a1aa',paddingTop:11},
  summaryTotal:{display:'flex',justifyContent:'space-between',color:'#fff',fontSize:19,borderTop:'1px solid #3a3a4d',marginTop:9,paddingTop:11},
  paidWith:{color:'#67e8f9',background:'#10323a',borderRadius:7,padding:8,marginTop:11,fontSize:11,fontWeight:800},
  search:{boxSizing:'border-box',width:'100%',background:'#0e0e16',border:'1px solid #39394d',borderRadius:8,padding:'9px 10px',color:'#fff',outline:'none',marginBottom:8},
  productGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:7,maxHeight:306,overflowY:'auto',overflowX:'hidden'},
  productChoice:{display:'grid',gridTemplateColumns:'36px minmax(0,1fr) 23px',alignItems:'center',gap:7,textAlign:'left',background:'#20202b',border:'1px solid #343447',borderRadius:8,padding:7,color:'#fff',cursor:'pointer',minWidth:0},
  productImage:{width:36,height:36,objectFit:'cover',borderRadius:6},
  productFallback:{width:36,height:36,display:'grid',placeItems:'center',background:'#292937',borderRadius:6},
  productChoiceCopy:{display:'flex',flexDirection:'column',minWidth:0},
  add:{color:'#a3e635',fontSize:22,fontWeight:900},
  manualAdd:{display:'flex',alignItems:'center',gap:6,marginTop:9,flexWrap:'wrap'},
  qty:{boxSizing:'border-box',width:64,background:'#0e0e16',border:'1px solid #39394d',borderRadius:8,padding:'9px 8px',color:'#fff'},
  detailFooter:{display:'flex',alignItems:'center',gap:7,padding:'13px 17px',borderTop:'1px solid #292939',flexWrap:'wrap'},
  footerSpacer:{flex:1},
  modal:{width:'min(580px,100%)',maxHeight:'calc(100dvh - 24px)',overflowY:'auto',overflowX:'hidden',background:'#14141f',border:'1px solid #3a3a4d',borderRadius:13,padding:17},
  modalHead:{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,marginBottom:15},
  modalTitle:{color:'#fff',fontSize:19,margin:0},
  two:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,195px),1fr))',gap:10},
  field:{display:'flex',flexDirection:'column',gap:6,marginBottom:10,minWidth:0},
  label:{color:'#a1a1aa',fontSize:12,fontWeight:900},
  input:{boxSizing:'border-box',width:'100%',minWidth:0,background:'#0e0e16',border:'1px solid #39394d',borderRadius:8,padding:'9px 10px',color:'#fff',outline:'none'},
  modalActions:{display:'flex',justifyContent:'flex-end',gap:7,marginTop:10,flexWrap:'wrap'},
  closeSummary:{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#20202b',borderRadius:8,padding:11,color:'#d4d4d8',marginBottom:11},
  totalFinal:{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#1d300c',border:'1px solid #4d7c0f',borderRadius:9,padding:13,color:'#d9f99d',fontSize:17},
  hint:{color:'#777788',fontSize:12,lineHeight:1.5},
  packagesList:{display:'flex',flexDirection:'column',gap:8,maxHeight:380,overflowY:'auto',paddingRight:4},
  packageRow:{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#20202b',border:'1px solid #343447',borderRadius:10,padding:'10px 14px',gap:10},
  packageInfo:{display:'flex',flexDirection:'column',flex:1,color:'#fff'},
  packageActions:{display:'flex',alignItems:'center',gap:8},
  qtyInput:{width:55,padding:'6px 8px',background:'#0e0e16',border:'1px solid #39394d',borderRadius:6,color:'#fff',textAlign:'center',fontSize:14,fontWeight:'bold'},
  primarySmall:{padding:'7px 12px',background:'#a3e635',color:'#09090d',border:0,borderRadius:6,fontWeight:800,cursor:'pointer',fontSize:13,whiteSpace:'nowrap'},
};
